import { PureComponent } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';

import { closeBusinessOnboardingPopUp } from 'state/actions/applicationUIActions';
import { submitBusinessOnboardingForm } from 'state/actions/formActions';

import { Button, Video } from 'components/base';
import { Form, PopUpCloseIcon, PopUpVideo } from 'styled';
import {
  Container,
  Background,
  PopUp,
  VideoPane,
  CloseButtonContainer,
  VideoPaneTitleContainer,
  VideoContainer,
  FormPane,
  FormTitleContainer,
  FormTitle,
  FormDescription,
  FormContainer,
  Form as StyledForm,
} from './style';

import Language from 'constants/Language';

import {
  GlobalState,
  BusinessOnboarding as IBusinessOnboarding,
  Status,
  FormValues,
} from 'types';

interface StoreProps {
  businessOnboardingPopUpIsActive: boolean;
  businessOnboarding: IBusinessOnboarding;
  submitBusinessOnboardingFormStatus: Status;
}

interface DispatchProps {
  actions: {
    closeBusinessOnboardingPopUp(): void;
    submitBusinessOnboardingForm(form: FormValues): void;
  };
}

export type Props = StoreProps & DispatchProps;

interface State {
  isShowingVideoPane: boolean;
  stopVideo: boolean;
  removePopUpWithAnimation: boolean;
  showOrHideVideoWithAnimation: boolean;
  hidePopUp: boolean;
}

class BusinessOnboardingPopUp extends PureComponent<Props, State> {
  state: State = {
    isShowingVideoPane: false,
    stopVideo: false,
    removePopUpWithAnimation: false,
    showOrHideVideoWithAnimation: false,
    hidePopUp: true,
  };

  /*
   * Pop up initially displays a form pane.
   * Upon form submission success, the video pane will take its place.
   */

  componentDidMount() {
    if (this.props.businessOnboardingPopUpIsActive) {
      this.setState({ hidePopUp: false });
    }
  }

  componentDidUpdate(prevProps: Props) {
    if (
      prevProps.submitBusinessOnboardingFormStatus === Status.PENDING &&
      this.props.submitBusinessOnboardingFormStatus === Status.FULFILLED
    ) {
      this.setState({
        isShowingVideoPane: true,
        showOrHideVideoWithAnimation: true,
      });
    }

    if (
      prevProps.businessOnboardingPopUpIsActive &&
      !this.props.businessOnboardingPopUpIsActive
    ) {
      this.setState({ removePopUpWithAnimation: true });
      setTimeout(() => this.setState({ hidePopUp: true }), 1000);
    }

    if (
      !prevProps.businessOnboardingPopUpIsActive &&
      this.props.businessOnboardingPopUpIsActive
    ) {
      this.setState({ hidePopUp: false });
    }
  }

  handleClosePopUp = () => {
    this.props.actions.closeBusinessOnboardingPopUp();
    this.setState({ stopVideo: true });
    setTimeout(
      () => this.setState({ isShowingVideoPane: false, stopVideo: false }),
      700
    );
  };

  render() {
    const {
      actions,
      businessOnboarding,
      businessOnboardingPopUpIsActive,
      submitBusinessOnboardingFormStatus,
    } = this.props;
    const {
      isShowingVideoPane,
      stopVideo,
      removePopUpWithAnimation,
      showOrHideVideoWithAnimation,
      hidePopUp,
    } = this.state;

    return (
      <Container
        hidePopUp={hidePopUp}
        popUpIsActive={businessOnboardingPopUpIsActive}
        removeWithAnimation={removePopUpWithAnimation}
        position="fixed"
        top={0}
        bottom={0}
        left={0}
        right={0}
        aria-hidden={!businessOnboardingPopUpIsActive}
      >
        <Background
          opacity={0.6}
          width="100%"
          height="100%"
          position="absolute"
          top={0}
          bottom={0}
          left={0}
          right={0}
        />
        <PopUp
          isShowingVideoPane={isShowingVideoPane}
          position="relative"
          width="100%"
          my={[0, 0, 0, 1.5]}
        >
          <VideoPane
            isShowingVideoPane={isShowingVideoPane}
            showOrHideWithAnimation={showOrHideVideoWithAnimation}
            width="100%"
            height="100%"
            aria-hidden={!isShowingVideoPane}
          >
            <CloseButtonContainer
              position="absolute"
              top={0}
              right={0}
              p={[1.5, 1.5, 1.5, 3]}
            >
              <Button
                ariaLabel={Language.t('Global.closePopUp')}
                onClick={this.handleClosePopUp}
              >
                <PopUpCloseIcon />
              </Button>
            </CloseButtonContainer>
            <VideoPaneTitleContainer p={[1.5, 1.5, 1.5, 3]}>
              <span>{Language.t('BusinessOnboardingPage.thankYou')}</span>
              <span>{Language.t('BusinessOnboardingPage.demoMessage')}</span>
            </VideoPaneTitleContainer>
            <VideoContainer flexGrow={1}>
              <PopUpVideo
                as={Video}
                video={businessOnboarding.popUpVideo}
                stopVideo={stopVideo}
                showVideoControl
              />
            </VideoContainer>
          </VideoPane>

          <FormPane
            isShowingVideoPane={isShowingVideoPane}
            position="absolute"
            top={0}
            overflowY="scroll"
            p={[1.5, 1.5, 1.5, 3]}
            aria-hidden={isShowingVideoPane}
          >
            <FormTitleContainer>
              <FormTitle>{businessOnboarding.popUpTitle}</FormTitle>
              <CloseButtonContainer>
                <Button
                  ariaLabel={Language.t('Global.closePopUp')}
                  onClick={this.handleClosePopUp}
                >
                  <PopUpCloseIcon />
                </Button>
              </CloseButtonContainer>
            </FormTitleContainer>
            <FormContainer
              display="flex"
              flexDirection={['column', 'column', 'column', 'row']}
              justifyItems={[
                'flex-start',
                'flex-start',
                'flex-start',
                'space-between',
              ]}
              mt={[1.5, 1.5, 1.5, 4]}
            >
              <FormDescription>
                {businessOnboarding.popUpDescription}
              </FormDescription>
              <StyledForm
                as={Form}
                inputFields={businessOnboarding.inputFields}
                mt={[1, 1, 1, 0]}
                flexGrow={1}
                color="gray"
                submitButtonLabel={Language.t(
                  'MarketingPageForm.formButtonLabel'
                )}
                handleSubmit={(formValues: FormValues) => {
                  actions.submitBusinessOnboardingForm(formValues);
                }}
                formSubmitStatus={submitBusinessOnboardingFormStatus}
                title="BusinessOnboardingPopUp"
              />
            </FormContainer>
          </FormPane>
        </PopUp>
      </Container>
    );
  }
}

const mapStateToProps = (state: GlobalState): StoreProps => ({
  businessOnboardingPopUpIsActive:
    state.applicationUI.businessOnboardingPopUpIsActive,
  businessOnboarding: state.globalSettings.businessOnboarding,
  submitBusinessOnboardingFormStatus:
    state.status.submitBusinessOnboardingFormStatus,
});

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => ({
  actions: bindActionCreators(
    {
      closeBusinessOnboardingPopUp,
      submitBusinessOnboardingForm,
    },
    dispatch
  ),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(BusinessOnboardingPopUp);
